import React, { useCallback, useEffect, useMemo, useState } from "react";
import { callApi, HttpMethod } from "src/services/apiService";
import { toast } from "sonner";
import Loading from "src/components/Loading";
import {
  Grid,
  GridColumn as Column,
  GridCellProps,
  GridItemChangeEvent,
  GridPageChangeEvent,
  GridSortChangeEvent,
  GridFilterChangeEvent,
} from "@progress/kendo-react-grid";
import { DropDownList, DropDownListChangeEvent } from "@progress/kendo-react-dropdowns";
import { SortDescriptor, orderBy, CompositeFilterDescriptor, filterBy } from "@progress/kendo-data-query";
import styles from "./ReassignedPools.module.css";

interface ReassignedPool {
  id: number;
  acctId: string;
  reassignedProductId: number;
  reassignedProductName: string;
  given: number;
  remaining: number | null;
  createdOn: string;
  monthsValid: number;
  blockSize: number | null;
  blockPrice: number | null;
  inEdit?: boolean;
  isNew?: boolean;
}

interface ReassignedProduct {
  id: number;
  name: string;
}

// Grid cell values can be raw edit strings (e.g. "12." while typing money); coerce to
// number|null for the API, which expects numeric blockSize/blockPrice.
const toNullableNumber = (v: unknown): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return isNaN(n) ? null : n;
};

// Build a LOCAL-midnight Date from the YYYY-MM-DD part of any date string. Avoids the
// UTC-vs-local skew you get from new Date("2027-06-01") (parsed as UTC) compared against
// new Date("2026-06-01T00:00:00") (parsed as local) — which caused false overlap hits.
const parseLocalDate = (s: string): Date => {
  const [y, m, d] = s.slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d);
};

// SQL Server DATEADD(MONTH, ...) clamps end-of-month. JS Date.setMonth overflows instead.
const addMonthsClamped = (date: Date, months: number): Date => {
  const y = date.getFullYear();
  const m = date.getMonth();
  const d = date.getDate();
  const firstOfTarget = new Date(y, m + months, 1);
  const daysInTarget = new Date(firstOfTarget.getFullYear(), firstOfTarget.getMonth() + 1, 0).getDate();
  firstOfTarget.setDate(Math.min(d, daysInTarget));
  return firstOfTarget;
};

const EDIT_FIELD = "inEdit";
const API_BASE = "sysadmin/reassigned-pools";
const PAGE_SIZES = [10, 25, 50, 100];
const PAGE_SIZE_KEY = "rp-pageSize";

const ReassignedPools: React.FC = () => {
  const [pools, setPools] = useState<ReassignedPool[]>([]);
  const [products, setProducts] = useState<ReassignedProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState<number | null>(null);
  const [originalItem, setOriginalItem] = useState<ReassignedPool | null>(null);

  const savedPageSize = Number(localStorage.getItem(PAGE_SIZE_KEY)) || 100;
  const [pageSize, setPageSize] = useState(savedPageSize);
  const [page, setPage] = useState({ skip: 0, take: savedPageSize });
  const [sort, setSort] = useState<SortDescriptor[]>([{ field: "createdOn", dir: "desc" }]);
  const [gridFilter, setGridFilter] = useState<CompositeFilterDescriptor | undefined>(undefined);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [poolsData, productsData] = await Promise.all([
        callApi(API_BASE, HttpMethod.GET),
        callApi(`${API_BASE}/products`, HttpMethod.GET),
      ]);
      setPools(poolsData);
      setProducts(productsData);
    } catch {
      toast.error("Failed to load reassigned pools.");
    } finally {
      setLoading(false);
    }
  };

  // ── Grid editing ──────────────────────────────────────────────────────

  const addNew = () => {
    if (editId !== null) return;
    if (products.length === 0) {
      toast.error("Cannot add pool until products are loaded.");
      return;
    }

    const defaultProduct = products[0];
    const newItem: ReassignedPool = {
      id: 0,
      acctId: "",
      reassignedProductId: defaultProduct.id,
      reassignedProductName: defaultProduct.name,
      given: 0,
      remaining: null,
      createdOn: new Date().toISOString().slice(0, 10),
      monthsValid: 12,
      blockSize: null,
      blockPrice: null,
      inEdit: true,
      isNew: true,
    };
    setPools([newItem, ...pools]);
    setEditId(0);
  };

  const enterEdit = (dataItem: ReassignedPool) => {
    if (editId !== null) return;
    setOriginalItem({ ...dataItem });
    setEditId(dataItem.id);
  };

  const cancelEdit = () => {
    if (originalItem) {
      setPools((prev) =>
        prev.map((item) =>
          item.id === originalItem.id ? { ...originalItem } : item,
        ),
      );
    } else {
      setPools((prev) => prev.filter((item) => !item.isNew));
    }
    setEditId(null);
    setOriginalItem(null);
  };

  const saveEdit = async (dataItem: ReassignedPool) => {
    if (dataItem.given <= 0) {
      toast.error("Given must be greater than 0.");
      return;
    }
    if (dataItem.monthsValid <= 0) {
      toast.error("Months valid must be greater than 0.");
      return;
    }

    // Block Size and Block Price are an all-or-nothing pair, and each must be positive.
    const blockSize = toNullableNumber(dataItem.blockSize);
    const blockPrice = toNullableNumber(dataItem.blockPrice);
    if ((blockSize === null) !== (blockPrice === null)) {
      toast.error("Block Size and Block Price must be set together, or both left empty.");
      return;
    }
    if (blockSize !== null && (!Number.isInteger(blockSize) || blockSize <= 0)) {
      toast.error("Block Size must be a positive whole number.");
      return;
    }
    if (blockPrice !== null && blockPrice <= 0) {
      toast.error("Block Price must be a positive number.");
      return;
    }

    // Pools for the same product cannot have overlapping date ranges
    // ([createdOn, createdOn + monthsValid months), half-open).
    const start = parseLocalDate(dataItem.createdOn);
    const end = addMonthsClamped(start, dataItem.monthsValid);
    const overlaps = pools.some((p) => {
      if (p.isNew || p.id === dataItem.id) return false;           // skip the row being saved
      if (p.reassignedProductId !== dataItem.reassignedProductId) return false;
      const ps = parseLocalDate(p.createdOn);
      const pe = addMonthsClamped(ps, p.monthsValid);
      return start < pe && ps < end;
    });
    if (overlaps) {
      toast.error("This date range overlaps an existing pool for the same product.");
      return;
    }

    try {
      if (dataItem.isNew) {
        await callApi(API_BASE, HttpMethod.POST, {
          reassignedProductId: dataItem.reassignedProductId,
          given: dataItem.given,
          monthsValid: dataItem.monthsValid,
          createdOn: dataItem.createdOn,
          blockSize,
          blockPrice,
        });
        toast.success("Pool created.");
      } else {
        await callApi(`${API_BASE}/${dataItem.id}`, HttpMethod.PUT, {
          reassignedProductId: dataItem.reassignedProductId,
          given: dataItem.given,
          remaining: dataItem.remaining,
          monthsValid: dataItem.monthsValid,
          createdOn: dataItem.createdOn,
          blockSize,
          blockPrice,
        });
        toast.success("Pool updated.");
      }
      setEditId(null);
      setOriginalItem(null);
      await loadData();
    } catch {
      toast.error("Failed to save pool.");
    }
  };

  const deletePool = async (id: number) => {
    if (!confirm("Delete this pool?")) return;
    try {
      await callApi(`${API_BASE}/${id}`, HttpMethod.DELETE);
      toast.success("Pool deleted.");
      await loadData();
    } catch {
      toast.error("Failed to delete pool.");
    }
  };

  const handleItemChange = (event: GridItemChangeEvent) => {
    const { dataItem, field, value } = event;
    if (!field) return;
    setPools((prev) =>
      prev.map((item) =>
        item.id === dataItem.id ? { ...item, [field]: value } : item,
      ),
    );
  };

  // ── Grid data pipeline ────────────────────────────────────────────────

  const dataWithEditFlag = useMemo(() => {
    return pools.map((item) => ({
      ...item,
      [EDIT_FIELD]: item.id === editId || item.isNew,
    }));
  }, [pools, editId]);

  const filteredData = useMemo(
    () => gridFilter ? filterBy(orderBy(dataWithEditFlag, sort), gridFilter) : orderBy(dataWithEditFlag, sort),
    [dataWithEditFlag, sort, gridFilter],
  );

  const pagedData = useMemo(
    () => filteredData.slice(page.skip, page.skip + page.take),
    [filteredData, page],
  );

  const syncPageSize = (newTake: number) => {
    if (newTake !== pageSize) {
      setPageSize(newTake);
      localStorage.setItem(PAGE_SIZE_KEY, String(newTake));
    }
  };

  const handlePageChange = (e: GridPageChangeEvent) => {
    syncPageSize(e.page.take);
    setPage({ skip: e.page.skip, take: e.page.take });
  };

  const handleSortChange = (e: GridSortChangeEvent) => {
    setSort(e.sort);
    setPage((p) => ({ ...p, skip: 0 }));
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setGridFilter(e.filter);
    setPage((p) => ({ ...p, skip: 0 }));
  };

  // ── Custom cells ──────────────────────────────────────────────────────

  const ProductCell = useCallback(
    (props: GridCellProps) => {
      const { dataItem } = props;
      if (dataItem[EDIT_FIELD]) {
        const selected = products.find((p) => p.id === dataItem.reassignedProductId);
        return (
          <td>
            <DropDownList
              data={products}
              textField="name"
              dataItemKey="id"
              value={selected || null}
              onChange={(e: DropDownListChangeEvent) => {
                const product = e.value as ReassignedProduct;
                handleItemChange({
                  dataItem,
                  field: "reassignedProductId",
                  value: product.id,
                  syntheticEvent: e.syntheticEvent,
                } as unknown as GridItemChangeEvent);
                handleItemChange({
                  dataItem: { ...dataItem, reassignedProductId: product.id },
                  field: "reassignedProductName",
                  value: product.name,
                  syntheticEvent: e.syntheticEvent,
                } as unknown as GridItemChangeEvent);
              }}
            />
          </td>
        );
      }
      return <td>{dataItem.reassignedProductName}</td>;
    },
    [products],
  );

  const ProductFilterCell = useCallback(
    (props: any) => {
      const options = ["All", ...products.map((p) => p.name)];
      const value = props.value || "All";
      return (
        <div className="k-filtercell">
          <DropDownList
            data={options}
            value={value}
            onChange={(e) => {
              const val = e.value === "All" ? "" : e.value;
              props.onChange({
                value: val,
                operator: val ? "eq" : "",
                syntheticEvent: e.syntheticEvent,
              });
            }}
          />
        </div>
      );
    },
    [products],
  );

  const NumberCell = useCallback((props: GridCellProps) => {
    const { dataItem, field } = props;
    const value = dataItem[field || ""];
    if (dataItem[EDIT_FIELD]) {
      return (
        <td>
          <input
            type="text"
            className="k-textbox k-input k-input-md k-rounded-md k-input-solid"
            value={value ?? ""}
            onChange={(e) => {
              const raw = e.target.value.replace(/,/g, "");
              const num = raw === "" ? null : Number(raw);
              handleItemChange({
                dataItem,
                field: field || "",
                value: num != null && !isNaN(num) ? num : raw,
                syntheticEvent: e,
              } as unknown as GridItemChangeEvent);
            }}
          />
        </td>
      );
    }
    return <td style={{ textAlign: "right" }}>{value != null ? Number(value).toLocaleString() : ""}</td>;
  }, []);

  const MoneyCell = useCallback((props: GridCellProps) => {
    const { dataItem, field } = props;
    const value = dataItem[field || ""];
    if (dataItem[EDIT_FIELD]) {
      return (
        <td>
          <input
            type="text"
            inputMode="decimal"
            className="k-textbox k-input k-input-md k-rounded-md k-input-solid"
            value={value ?? ""}
            onChange={(e) => {
              // Keep the raw text (digits + a single dot, max 2 decimals) so intermediate
              // states like "12." and "12.5" survive. Coerced to a number at save time.
              // (Coercing here would turn "12." into 12 and eat the decimal point.)
              let raw = e.target.value.replace(/[^0-9.]/g, "");
              const dot = raw.indexOf(".");
              if (dot !== -1) {
                raw = raw.slice(0, dot + 1) + raw.slice(dot + 1).replace(/\./g, "").slice(0, 2);
              }
              handleItemChange({
                dataItem,
                field: field || "",
                value: raw === "" ? null : raw,
                syntheticEvent: e,
              } as unknown as GridItemChangeEvent);
            }}
          />
        </td>
      );
    }
    return (
      <td style={{ textAlign: "right" }}>
        {value != null && value !== "" ? `$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ""}
      </td>
    );
  }, []);

  const DateCell = useCallback((props: GridCellProps) => {
    const { dataItem, field } = props;
    const value = dataItem[field || ""];
    if (dataItem[EDIT_FIELD]) {
      const dateVal = value ? (value instanceof Date ? value : new Date(value)) : new Date();
      const isoDate = dateVal.toISOString().slice(0, 10);
      return (
        <td>
          <input
            type="date"
            className="k-textbox k-input k-input-md k-rounded-md k-input-solid"
            value={isoDate}
            onChange={(e) => {
              handleItemChange({
                dataItem,
                field: field || "",
                value: e.target.value,
                syntheticEvent: e,
              } as unknown as GridItemChangeEvent);
            }}
          />
        </td>
      );
    }
    if (!value) return <td />;
    const date = value instanceof Date ? value : new Date(value);
    return <td>{date.toLocaleDateString()}</td>;
  }, []);

  const CommandCell = useCallback(
    (props: GridCellProps) => {
      const { dataItem } = props;
      const isInEdit = dataItem[EDIT_FIELD];
      return (
        <td>
          {isInEdit ? (
            <div className="d-flex gap-2">
              <button className="btn btn-sm btn-primary" onClick={() => saveEdit(dataItem)}>
                {dataItem.isNew ? "Add" : "Update"}
              </button>
              <button className="btn btn-sm btn-secondary" onClick={cancelEdit}>
                Cancel
              </button>
            </div>
          ) : (
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => enterEdit(dataItem)}
                disabled={editId !== null}
              >
                Edit
              </button>
              <button
                className="btn btn-sm btn-outline-danger"
                onClick={() => deletePool(dataItem.id)}
                disabled={editId !== null}
              >
                Delete
              </button>
            </div>
          )}
        </td>
      );
    },
    [editId],
  );

  if (loading) return <Loading />;

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <button className="btn btn-success btn-sm" onClick={addNew} disabled={editId !== null}>
          + Add Pool
        </button>
      </div>

      <div className="mt-3">
        <Grid
          className="sysadmin-grid"
          data={pagedData}
          total={filteredData.length}
          skip={page.skip}
          take={page.take}
          dataItemKey="id"
          sortable={true}
          resizable={true}
          sort={sort}
          onSortChange={handleSortChange}
          filterable={true}
          filter={gridFilter}
          onFilterChange={handleFilterChange}
          pageable={{ pageSizes: PAGE_SIZES }}
          onPageChange={handlePageChange}
          editField={EDIT_FIELD}
          onItemChange={handleItemChange}
          style={{ height: "calc(100vh - 135px)", width: "100%" }}
        >
          <Column
            field="reassignedProductName"
            title="Product"
            width={250}
            cells={{ data: ProductCell }}
            filterCell={ProductFilterCell}
          />
          <Column field="given" title="Given" width={170} filterable={false} cells={{ data: NumberCell }} />
          <Column field="remaining" title="Remaining" width={170} filterable={false} cells={{ data: NumberCell }} />
          <Column field="blockSize" title="Block Size" width={140} filterable={false} cells={{ data: NumberCell }} />
          <Column field="blockPrice" title="Block Price" width={140} filterable={false} cells={{ data: MoneyCell }} />
          <Column field="monthsValid" title="Months Valid" width={140} filterable={false} />
          <Column field="createdOn" title="Created On" width={150} cells={{ data: DateCell }} filterable={false} />
          <Column
            title="Actions"
            width={200}
            filterable={false}
            sortable={false}
            cells={{ data: CommandCell }}
          />
        </Grid>
      </div>
    </div>
  );
};

export default ReassignedPools;
